import { createAsyncThunk, createDraftSafeSelector, createSlice } from "@reduxjs/toolkit";
import BigNumber from "bignumber.js";
import vaultsConfig from "src/configs/classic-farming-vaults";
import { BASE18, FS } from "src/configs/constance";
import NFTCollectionContract from "src/contract-logic/logic/NFTCollectionContract";
import NFTCoreContract from "src/contract-logic/logic/NFTCoreContract";
import NFTVaultContract from "src/contract-logic/logic/NFTVaultContract";
import { RarityMapping } from "src/utils/knight";
import multiCall from "src/utils/multicall";

export const fetchAllVaultsData = createAsyncThunk("classicFarmingSlice/fetchAllVaultsData", async (_, thunkAPI) => {
  await Promise.all(Object.entries(vaultsConfig).map(([vaultId, _]) => thunkAPI.dispatch(fetchVaultData({ vaultId }))));
});

export const fetchVaultData = createAsyncThunk("classicFarmingSlice/fetchVaultData", async ({ vaultId }, thunkAPI) => {
  const accountAddress = thunkAPI.getState().accountSlice.address;
  const vault = vaultsConfig[vaultId];
  let totalNFTs = await NFTCollectionContract.getBalance(vault.contractAddress);
  const contract = NFTVaultContract.getContractFactory(vault.contractAddress, { decimals: 18 });
  const assetData = await contract.assetData();
  const weeklyReward = BigNumber(assetData.emissionPerSecond.toString())
    .multipliedBy(7 * 24 * 3600)
    .dividedBy(BASE18)
    .toNumber();
  let c = totalNFTs || 1;
  let apr = (weeklyReward / (c * vault.collectionPrice) / 7) * 365 * 100;
  let isStaked = false;
  if (accountAddress) {
    isStaked = await contract.isStaked(accountAddress);
    thunkAPI.dispatch(fetchStakedCollection({ vaultId }));
  }
  thunkAPI.dispatch(fetchUserClaimableReward({ vaultId }));
  vault.polishRequired && thunkAPI.dispatch(fetchUserDecayStatus({ vaultId }));
  return { ...vault, totalNFTs, isStaked, apr };
});

export const fetchUserClaimableReward = createAsyncThunk(
  "classicFarmingSlice/fetchUserClaimableReward",
  async ({ vaultId }, thunkAPI) => {
    const vault = vaultsConfig[vaultId];
    const accountAddress = thunkAPI.getState().accountSlice.address;
    const contract = NFTVaultContract.getContractFactory(vault.contractAddress, { decimals: 18 });
    const reward = await contract.getTotalRewardsBalance(accountAddress);
    return reward.toNumber();
  }
);

export const fetchUserDecayStatus = createAsyncThunk(
  "classicFarmingSlice/fetchUserDecayStatus",
  async ({ vaultId }, thunkAPI) => {
    const vault = vaultsConfig[vaultId];
    const accountAddress = thunkAPI.getState().accountSlice.address;
    const contract = NFTVaultContract.getContractFactory(vault.contractAddress, { decimals: 18 });
    const decayStatus = await contract.getCurrentDecayStatus(accountAddress);
    return decayStatus.toNumber();
  }
);

export const fetchStakedCollection = createAsyncThunk(
  "classicFarmingSlice/fetchStakedCollection",
  async ({ vaultId }, thunkAPI) => {
    const vault = vaultsConfig[vaultId];
    const accountAddress = thunkAPI.getState().accountSlice.address;
    const contract = NFTVaultContract.getContractFactory(vault.contractAddress, { decimals: 18 });
    const id = await contract.getStakedCollectionId(accountAddress);
    const [collectionMetadata, depositTime, currentExp, attainedExp] = await Promise.all([
      NFTCollectionContract.getCollectionMetadata(id),
      contract.getDepositTime(accountAddress),
      contract.getCurrentExperience(accountAddress),
      NFTCollectionContract.getCollectionExperience(id),
    ]);

    const itemIdList = [
      collectionMetadata.armorTokenId,
      collectionMetadata.helmetTokenId,
      collectionMetadata.shieldTokenId,
      collectionMetadata.weaponTokenId,
    ];
    const [itemsMetadata] = await Promise.all([
      multiCall(
        NFTCoreContract.ABI,
        itemIdList.map((tokenId) => ({
          address: NFTCoreContract.ADDRESS,
          name: "getTokenMetadata",
          params: [tokenId],
        }))
      ),
    ]);

    let armorMetadata = itemsMetadata[0];
    let helmetMetadata = itemsMetadata[1];
    let shieldMetadata = itemsMetadata[2];
    let weaponMetadata = itemsMetadata[3];

    return {
      rarity: collectionMetadata.rarity >= 1 ? RarityMapping[collectionMetadata.rarity - 1] : "",
      id,
      exp: currentExp,
      attainedExp,
      depositTime: depositTime * 1000,
      armor: {
        tokenId: parseInt(collectionMetadata.armorTokenId),
        rarity: parseInt(armorMetadata[0].tokenRarity),
        exp: parseInt(armorMetadata[0].experiencePoint),
      },
      helmet: {
        tokenId: parseInt(collectionMetadata.helmetTokenId),
        rarity: parseInt(helmetMetadata[0].tokenRarity),
        exp: parseInt(helmetMetadata[0].experiencePoint),
      },
      shield: {
        tokenId: parseInt(collectionMetadata.shieldTokenId),
        rarity: parseInt(shieldMetadata[0].tokenRarity),
        exp: parseInt(shieldMetadata[0].experiencePoint),
      },
      weapon: {
        tokenId: parseInt(collectionMetadata.weaponTokenId),
        rarity: parseInt(weaponMetadata[0].tokenRarity),
        exp: parseInt(weaponMetadata[0].experiencePoint),
      },
    };
  }
);

const initialState = {
  vaults: {},
  fetchingStatus: FS.INITIAL,
};

const classicFarmingSlice = createSlice({
  name: "classicFarmingSlice",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(fetchAllVaultsData.pending, (state, _) => {
        state.fetchingStatus = FS.FETCHING;
      })
      .addCase(fetchAllVaultsData.rejected, (state, action) => {
        state.fetchingStatus = FS.FAIL;
        console.error(action);
      })
      .addCase(fetchAllVaultsData.fulfilled, (state, _) => {
        state.fetchingStatus = FS.SUCCESS;
      })
      .addCase(fetchVaultData.fulfilled, (state, action) => {
        const vaultId = action.meta.arg.vaultId;
        state.vaults[vaultId] = { ...state.vaults[vaultId], ...action.payload };
      })
      .addCase(fetchUserClaimableReward.fulfilled, (state, action) => {
        const vaultId = action.meta.arg.vaultId;
        state.vaults[vaultId] = { ...state.vaults[vaultId], claimableReward: action.payload };
      })
      .addCase(fetchUserClaimableReward.rejected, (state, action) => {
        const vaultId = action.meta.arg.vaultId;
        state.vaults[vaultId] = { ...state.vaults[vaultId], claimableReward: null };
        console.error(action);
      })
      .addCase(fetchUserDecayStatus.fulfilled, (state, action) => {
        const vaultId = action.meta.arg.vaultId;
        state.vaults[vaultId] = { ...state.vaults[vaultId], decayStatus: action.payload };
      })
      .addCase(fetchUserDecayStatus.rejected, (state, action) => {
        const vaultId = action.meta.arg.vaultId;
        state.vaults[vaultId] = { ...state.vaults[vaultId], decayStatus: null };
        console.error(action);
      })
      .addCase(fetchStakedCollection.fulfilled, (state, action) => {
        const vaultId = action.meta.arg.vaultId;
        state.vaults[vaultId] = { ...state.vaults[vaultId], collection: action.payload };
      })
      .addCase(fetchStakedCollection.rejected, (state, action) => {
        console.error(action);
        const vaultId = action.meta.arg.vaultId;
        if (state.vaults[vaultId]) {
          state.vaults[vaultId].collection = undefined;
        }
      }),
});

export default classicFarmingSlice.reducer;

export const selectClassicFarmingSlice = (state) => state.classicFarmingSlice;

export const selectAllVaults = createDraftSafeSelector(
  (state) => selectClassicFarmingSlice(state).vaults,
  (vaults) => {
    if (!vaults) return [];
    const res = Object.entries(vaults).map(([_, item]) => item);
    res.sort((v1, v2) => v1.order - v2.order);
    return res;
  }
);

export const selectVaultById = createDraftSafeSelector(
  (state) => selectClassicFarmingSlice(state).vaults,
  (_, props) => props.vaultId,
  (vaults, vaultId) => {
    return vaults[vaultId];
  }
);
